import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { 
  Download, 
  FileSpreadsheet, 
  Calendar, 
  CalendarDays, 
  CalendarRange,
  Loader2,
  Printer,
  FileText
} from "lucide-react";
import { exportToExcel, prepareCostingDataForExport } from "@/utils/exportUtils";
import { DMYDateInput } from "@/components/ui/dmy-date-picker";

import { formatDMY } from "@/lib/dmy";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Generate month options (last 12 months)
const getMonthOptions = () => {
  const options = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = String(d.getFullYear()).slice(-2);
    const label = d.toLocaleString('default', { month: 'long', year: 'numeric' });
    options.push({ value: `${month}${year}`, label });
  }
  return options;
};

// Generate year options (last 5 years)
const getYearOptions = () => {
  const options = [];
  const now = new Date();
  for (let i = 0; i < 5; i++) {
    const year = now.getFullYear() - i;
    const yy = String(year).slice(-2);
    options.push({ value: yy, label: year.toString() });
  }
  return options;
};

const CostingExportDialog = ({ 
  open, 
  onOpenChange, 
  token, 
  currentFilters = {},
  totalErrors = 0,
  mode = "excel" // "excel" or "print"
}) => {
  const [exportMode, setExportMode] = useState("all");
  const [errorType, setErrorType] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const [loading, setLoading] = useState(false);
  const [previewCount, setPreviewCount] = useState(null);

  const monthOptions = getMonthOptions();
  const yearOptions = getYearOptions();

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setExportMode("all");
      setErrorType("all");
      setDateFrom(currentFilters.dateFrom || "");
      setDateTo(currentFilters.dateTo || "");
      setSelectedMonth(monthOptions[0]?.value || "");
      setSelectedYear(yearOptions[0]?.value || "");
      setPreviewCount(null);
    }
  }, [open, currentFilters]);

  // Fetch preview count when filters change
  useEffect(() => {
    if (!open || !token) return;
    
    const fetchCount = async () => {
      try {
        const params = new URLSearchParams();
        params.set("max_records", "1"); // Just need count
        
        if (currentFilters.clientCode) params.set("client_code", currentFilters.clientCode);
        if (currentFilters.search) params.set("search", currentFilters.search);
        
        if (errorType !== "all") params.set("error_type", errorType);
        
        if (exportMode === "date_range") {
          if (dateFrom) params.set("date_from", dateFrom);
          if (dateTo) params.set("date_to", dateTo);
          params.set("export_mode", "date_range");
        } else if (exportMode === "month") {
          params.set("month", selectedMonth);
          params.set("export_mode", "month");
        } else if (exportMode === "year") {
          params.set("year", selectedYear);
          params.set("export_mode", "year");
        } else {
          params.set("export_mode", "all");
        }
        
        const res = await fetch(`${BACKEND_URL}/api/errors/costing/export-all?${params}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (res.ok) {
          const data = await res.json();
          setPreviewCount(data.total);
        }
      } catch (e) {
        console.error("Error fetching preview count:", e);
      }
    };
    
    const timer = setTimeout(fetchCount, 300);
    return () => clearTimeout(timer);
  }, [open, token, exportMode, errorType, dateFrom, dateTo, selectedMonth, selectedYear, currentFilters]);

  const handleExport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("max_records", "10000");
      
      if (currentFilters.clientCode) params.set("client_code", currentFilters.clientCode);
      if (currentFilters.search) params.set("search", currentFilters.search);
      
      if (errorType !== "all") params.set("error_type", errorType);
      
      if (exportMode === "date_range") {
        if (dateFrom) params.set("date_from", dateFrom);
        if (dateTo) params.set("date_to", dateTo);
        params.set("export_mode", "date_range");
      } else if (exportMode === "month") {
        params.set("month", selectedMonth);
        params.set("export_mode", "month");
      } else if (exportMode === "year") {
        params.set("year", selectedYear);
        params.set("export_mode", "year");
      } else {
        params.set("export_mode", "all");
      }
      
      const res = await fetch(`${BACKEND_URL}/api/errors/costing/export-all?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error("Failed to fetch data");
      
      const data = await res.json();
      const errors = data.errors || [];
      
      if (errors.length === 0) {
        toast.warning("No errors found with selected filters");
        return;
      }
      
      // Build title based on filters
      let title = "Costing Errors";
      if (exportMode === "month") {
        const monthOpt = monthOptions.find(m => m.value === selectedMonth);
        title += ` - ${monthOpt?.label || selectedMonth}`;
      } else if (exportMode === "year") {
        title += ` - Year 20${selectedYear}`;
      } else if (exportMode === "date_range" && (dateFrom || dateTo)) {
        title += ` - ${dateFrom || "Start"} to ${dateTo || "End"}`;
      }
      
      if (errorType !== "all") {
        title += ` (${errorType} Only)`;
      }
      
      if (mode === "excel") {
        // Excel Export
        const { headers, rows, summaryStats } = prepareCostingDataForExport(errors, {
          ...currentFilters,
          dateFrom,
          dateTo
        });
        
        exportToExcel(rows, {
          fileName: `EAC_Costing_Errors_${exportMode}_${new Date().toISOString().slice(0,10)}`,
          sheetName: 'Costing Errors',
          headers,
          title,
          summaryStats,
          columnWidths: [20, 15, 30, 40, 12, 10, 8, 12]
        });
        
        toast.success(`Exported ${errors.length} costing errors to Excel`);
        onOpenChange(false);
      } else {
        // Print Preview - Open in new tab
        openPrintPreview(errors, title, data.filters);
      }
      
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export data");
    } finally {
      setLoading(false);
    }
  };

  const openPrintPreview = (errors, title, filters) => {
    // Create print preview HTML
    const printContent = generatePrintHTML(errors, title, filters);
    
    // Try multiple approaches to open print preview
    
    // Approach 1: Create a Blob URL (works better on mobile)
    try {
      const blob = new Blob([printContent], { type: 'text/html' });
      const blobUrl = URL.createObjectURL(blob);
      
      // Try to open in new tab
      const newTab = window.open(blobUrl, '_blank');
      
      if (newTab) {
        // Success - clean up blob URL after a delay
        setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
        onOpenChange(false);
        toast.success("Print preview opened in new tab");
        return;
      }
    } catch (e) {
      console.log("Blob approach failed:", e);
    }
    
    // Approach 2: If popup blocked, navigate in same tab with history
    try {
      // Store data and navigate to print page
      const printData = {
        errors,
        title,
        filters,
        generatedAt: new Date().toISOString()
      };
      sessionStorage.setItem('printPreviewData', JSON.stringify(printData));
      
      // Open print page in same window (user can use back button)
      const printPageUrl = `/print-preview?source=costing`;
      window.open(printPageUrl, '_blank') || window.location.assign(printPageUrl);
      onOpenChange(false);
      return;
    } catch (e) {
      console.log("Session storage approach failed:", e);
    }
    
    // Approach 3: Last resort - download as HTML file
    try {
      const blob = new Blob([printContent], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Costing_Errors_${title.replace(/[^a-zA-Z0-9]/g, '_')}.html`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      onOpenChange(false);
      toast.success("Print preview downloaded as HTML file - open it to print");
      return;
    } catch (e) {
      console.log("Download approach failed:", e);
    }
    
    toast.error("Unable to open print preview. Please try on desktop browser.");
  };

  const generatePrintHTML = (errors, title, filters) => {
    const now = new Date().toLocaleString();
    
    // Group errors by error type for summary
    const productErrors = errors.filter(e => e.errors?.some(err => err.includes('Product') || err.includes('Price')));
    const shippingErrors = errors.filter(e => e.errors?.some(err => err.includes('Shipping') || err.includes('Service')));
    const weightErrors = errors.filter(e => e.errors?.some(err => err.includes('Weight')));
    const countryErrors = errors.filter(e => e.errors?.some(err => err.includes('Country')));
    
    // Generate table rows
    const tableRows = errors.map((error, idx) => `
      <tr style="border-bottom: 1px solid #e2e8f0;">
        <td style="padding: 8px; font-family: monospace; font-size: 11px;">${error.tracking || '-'}</td>
        <td style="padding: 8px; font-size: 12px;">${error.client_code || '-'}</td>
        <td style="padding: 8px; font-size: 12px; max-width: 200px;">
          ${error.second_name || error.product || '-'}
          ${error.product_count > 1 ? `<span style="background: #e2e8f0; padding: 2px 6px; border-radius: 4px; font-size: 10px; margin-left: 4px;">+${error.product_count - 1}</span>` : ''}
        </td>
        <td style="padding: 8px; font-size: 11px; color: #dc2626;">
          ${(error.errors || []).slice(0, 2).map(e => `• ${e.length > 40 ? e.substring(0, 40) + '...' : e}`).join('<br>')}
          ${(error.errors || []).length > 2 ? `<br><span style="color: #64748b;">+${error.errors.length - 2} more</span>` : ''}
        </td>
        <td style="padding: 8px; font-size: 12px; text-align: center;">${formatDMY(error.order_date)}</td>
      </tr>
    `).join('');
    
    return `
<!DOCTYPE html>
<html>
<head>
  <title>${title} - EximFlow</title>
  <style>
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; margin: 0; padding: 20px; background: #f8fafc; }
    .container { max-width: 1200px; margin: 0 auto; background: white; border-radius: 8px; box-shadow: 0 1px 3px rgba(0,0,0,0.1); }
    .header { padding: 24px; border-bottom: 2px solid #e2e8f0; }
    .header h1 { margin: 0 0 8px 0; color: #1e293b; font-size: 24px; }
    .header .meta { color: #64748b; font-size: 14px; }
    .toolbar { padding: 16px 24px; background: #f1f5f9; display: flex; gap: 12px; justify-content: flex-end; }
    .toolbar button { padding: 10px 20px; border: none; border-radius: 6px; cursor: pointer; font-size: 14px; font-weight: 500; display: flex; align-items: center; gap: 8px; }
    .btn-pdf { background: #dc2626; color: white; }
    .btn-pdf:hover { background: #b91c1c; }
    .btn-excel { background: #16a34a; color: white; }
    .btn-excel:hover { background: #15803d; }
    .btn-print { background: #2563eb; color: white; }
    .btn-print:hover { background: #1d4ed8; }
    .summary { padding: 20px 24px; display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; border-bottom: 1px solid #e2e8f0; }
    .summary-card { background: #f8fafc; border-radius: 8px; padding: 16px; text-align: center; }
    .summary-card .value { font-size: 28px; font-weight: 700; color: #1e293b; }
    .summary-card .label { font-size: 12px; color: #64748b; margin-top: 4px; }
    .filters { padding: 16px 24px; background: #fffbeb; border-bottom: 1px solid #fde68a; font-size: 13px; color: #92400e; }
    table { width: 100%; border-collapse: collapse; }
    thead { background: #f1f5f9; }
    th { padding: 12px 8px; text-align: left; font-size: 12px; font-weight: 600; color: #475569; border-bottom: 2px solid #e2e8f0; }
    tbody tr:hover { background: #f8fafc; }
    .footer { padding: 16px 24px; text-align: center; color: #94a3b8; font-size: 12px; border-top: 1px solid #e2e8f0; }
    @media print { 
      .toolbar { display: none; } 
      body { background: white; padding: 0; }
      .container { box-shadow: none; }
    }
  </style>
  <script src="https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js"></script>
  <script src="https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js"></script>
  <script src="https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.5.31/jspdf.plugin.autotable.min.js"></script>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1>📋 ${title}</h1>
      <div class="meta">
        Generated: ${now} | Total Records: ${errors.length}
        ${filters?.client_code ? ` | Client: ${filters.client_code}` : ''}
      </div>
    </div>
    
    <div class="toolbar" id="toolbar">
      <button class="btn-pdf" onclick="exportToPDF()">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
        Download PDF
      </button>
      <button class="btn-excel" onclick="exportToExcel()">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/><polyline points="14 2 14 8 20 8"/><path d="M10 12l4 4m0-4l-4 4"/></svg>
        Download Excel
      </button>
      <button class="btn-print" onclick="window.print()">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
        Print
      </button>
    </div>
    
    <div class="summary">
      <div class="summary-card">
        <div class="value">${errors.length}</div>
        <div class="label">Total Errors</div>
      </div>
      <div class="summary-card">
        <div class="value" style="color: #dc2626;">${productErrors.length}</div>
        <div class="label">Product Issues</div>
      </div>
      <div class="summary-card">
        <div class="value" style="color: #ea580c;">${shippingErrors.length}</div>
        <div class="label">Shipping Issues</div>
      </div>
      <div class="summary-card">
        <div class="value" style="color: #ca8a04;">${weightErrors.length + countryErrors.length}</div>
        <div class="label">Weight/Country</div>
      </div>
    </div>
    
    ${filters && (filters.date_from || filters.date_to || filters.month || filters.year) ? `
    <div class="filters">
      <strong>Filters Applied:</strong>
      ${filters.date_from ? ` From: ${filters.date_from}` : ''}
      ${filters.date_to ? ` To: ${filters.date_to}` : ''}
      ${filters.month ? ` Month: ${filters.month}` : ''}
      ${filters.year ? ` Year: 20${filters.year}` : ''}
    </div>
    ` : ''}
    
    <div style="overflow-x: auto;">
      <table>
        <thead>
          <tr>
            <th style="width: 140px;">Tracking</th>
            <th style="width: 100px;">Client</th>
            <th style="width: 200px;">Product</th>
            <th>Error Details</th>
            <th style="width: 100px; text-align: center;">Date</th>
          </tr>
        </thead>
        <tbody>
          ${tableRows}
        </tbody>
      </table>
    </div>
    
    <div class="footer">
      EximFlow - Costing Error Report | Page 1 of 1
    </div>
  </div>
  
  <script>
    const errorData = ${JSON.stringify(errors)};
    const reportTitle = "${title}";
    
    function exportToPDF() {
      const { jsPDF } = window.jspdf;
      const doc = new jsPDF('l', 'mm', 'a4');
      
      doc.setFontSize(16);
      doc.text(reportTitle, 14, 15);
      doc.setFontSize(10);
      doc.text('Generated: ${now}', 14, 22);
      
      const tableData = errorData.map(e => [
        e.tracking || '-',
        e.client_code || '-',
        (e.second_name || e.product || '-').substring(0, 30),
        (e.errors || []).slice(0, 2).join('; ').substring(0, 60),
        e.order_date || '-'
      ]);
      
      doc.autoTable({
        head: [['Tracking', 'Client', 'Product', 'Error', 'Date']],
        body: tableData,
        startY: 28,
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [59, 130, 246] }
      });
      
      doc.save('costing_errors_${new Date().toISOString().slice(0,10)}.pdf');
    }
    
    function exportToExcel() {
      const wsData = [
        ['${title}'],
        ['Generated: ${now}'],
        [],
        ['Tracking', 'Client', 'Product', 'Qty', 'Error Details', 'Date', 'Country', 'Service']
      ];
      
      errorData.forEach(e => {
        wsData.push([
          e.tracking || '',
          e.client_code || '',
          e.second_name || e.product || '',
          e.product_count || 1,
          (e.errors || []).join('; '),
          e.order_date || '',
          e.country || '',
          e.service || ''
        ]);
      });
      
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet(wsData);
      
      ws['!cols'] = [
        {wch: 20}, {wch: 12}, {wch: 30}, {wch: 6}, {wch: 50}, {wch: 10}, {wch: 15}, {wch: 12}
      ];
      
      XLSX.utils.book_append_sheet(wb, ws, 'Costing Errors');
      XLSX.writeFile(wb, 'costing_errors_${new Date().toISOString().slice(0,10)}.xlsx');
    }
  </script>
</body>
</html>
    `;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {mode === "excel" ? (
              <><FileSpreadsheet className="w-5 h-5 text-green-600" /> Export Costing Errors</>
            ) : (
              <><Printer className="w-5 h-5 text-blue-600" /> Print Costing Errors</>
            )}
          </DialogTitle>
          <DialogDescription>
            {mode === "excel" 
              ? "Export all costing errors to Excel with date filtering options"
              : "Open print preview in new tab with PDF/Excel download options"
            }
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Date Mode Selection */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Export Date Range</Label>
            <RadioGroup value={exportMode} onValueChange={setExportMode} className="grid grid-cols-2 gap-2">
              <div className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-slate-50 cursor-pointer">
                <RadioGroupItem value="all" id="all" />
                <Label htmlFor="all" className="cursor-pointer flex items-center gap-2">
                  <CalendarRange className="w-4 h-4 text-slate-500" />
                  All Records
                </Label>
              </div>
              <div className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-slate-50 cursor-pointer">
                <RadioGroupItem value="date_range" id="date_range" />
                <Label htmlFor="date_range" className="cursor-pointer flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-blue-500" />
                  Date Range
                </Label>
              </div>
              <div className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-slate-50 cursor-pointer">
                <RadioGroupItem value="month" id="month" />
                <Label htmlFor="month" className="cursor-pointer flex items-center gap-2">
                  <CalendarDays className="w-4 h-4 text-green-500" />
                  By Month
                </Label>
              </div>
              <div className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-slate-50 cursor-pointer">
                <RadioGroupItem value="year" id="year" />
                <Label htmlFor="year" className="cursor-pointer flex items-center gap-2">
                  <FileText className="w-4 h-4 text-purple-500" />
                  By Year
                </Label>
              </div>
            </RadioGroup>
          </div>
          
          {/* Conditional date inputs */}
          {exportMode === "date_range" && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-500">From Date</Label>
                <DMYDateInput value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">To Date</Label>
                <DMYDateInput value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="mt-1" />
              </div>
            </div>
          )}
          
          {exportMode === "month" && (
            <div>
              <Label className="text-xs text-slate-500">Select Month</Label>
              <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select month" />
                </SelectTrigger>
                <SelectContent>
                  {monthOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          
          {exportMode === "year" && (
            <div>
              <Label className="text-xs text-slate-500">Select Year</Label>
              <Select value={selectedYear} onValueChange={setSelectedYear}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select year" />
                </SelectTrigger>
                <SelectContent>
                  {yearOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          
          {/* Error Type Filter */}
          <div>
            <Label className="text-xs text-slate-500">Error Type Filter</Label>
            <Select value={errorType} onValueChange={setErrorType}>
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="All error types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Error Types</SelectItem>
                <SelectItem value="Product">Product Issues Only</SelectItem>
                <SelectItem value="Shipping">Shipping Issues Only</SelectItem>
                <SelectItem value="Weight">Weight Issues Only</SelectItem>
                <SelectItem value="Country">Country Issues Only</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Preview Count */}
          <div className="bg-slate-50 rounded-lg p-3 flex items-center justify-between">
            <span className="text-sm text-slate-600">Records to export:</span>
            <Badge variant="outline" className="text-lg px-3">
              {previewCount !== null ? previewCount.toLocaleString() : '...'}
            </Badge>
          </div>
        </div>
        
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleExport} 
            disabled={loading || previewCount === 0}
            className={mode === "excel" ? "bg-green-600 hover:bg-green-700" : "bg-blue-600 hover:bg-blue-700"}
          >
            {loading ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</>
            ) : mode === "excel" ? (
              <><Download className="w-4 h-4 mr-2" /> Export Excel</>
            ) : (
              <><Printer className="w-4 h-4 mr-2" /> Open Print Preview</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CostingExportDialog;
